import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { X, CheckCircle, AlertTriangle } from 'react-feather';

// Initialize Stripe - will be loaded with publishable key from settings
let stripePromise = null;

const getStripe = (publishableKey) => {
  if (!stripePromise && publishableKey) {
    console.log('[Checkout] Initializing Stripe with publishable key:', publishableKey.substring(0, 20) + '...');
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

// Normalize API base to avoid duplicate /api/api paths in production
// Fixed version that properly handles various URL formats
const normalizeApiBase = (rawUrl) => {
  const fallback = 'http://localhost:5000/api';
  
  if (!rawUrl) {
    console.log('[Checkout] No API URL provided, using fallback:', fallback);
    return fallback;
  }
  
  // Remove trailing slashes and clean up
  let input = rawUrl.trim().replace(/\/+$/, '');
  
  // Remove any /api/api duplicates
  input = input.replace(/\/api\/api\//i, '/api/');
  input = input.replace(/\/api\/api$/i, '/api');
  
  // If it doesn't end with /api, add it
  if (!/\/api$/i.test(input)) {
    input = input.replace(/\/api\/?$/i, '') + '/api';
  }
  
  console.log('[Checkout] Normalized API_BASE from:', rawUrl, 'to:', input);
  return input;
};

const API_BASE = normalizeApiBase(process.env.REACT_APP_API_URL);

// Centralized token retrieval to avoid checkout auth mismatch in production
const getFreshToken = () => {
  // Try multiple storage keys that might contain the token
  const tokenKeys = ['streamToken', 'token', 'authToken', 'accessToken', 'jwt'];
  
  for (const key of tokenKeys) {
    const token = localStorage.getItem(key);
    if (token) {
      console.log('[Checkout] Found token in localStorage key:', key);
      return token;
    }
  }

  // Try userDetails object
  try {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const nestedToken = userDetails?.token || userDetails?.accessToken || userDetails?.jwt;
    if (nestedToken) {
      console.log('[Checkout] Found token in userDetails object');
      return nestedToken;
    }
  } catch {
    // Ignore parse errors
  }
  
  console.log('[Checkout] WARNING: No authentication token found!');
  return null;
};

const getAuthHeaders = () => {
  const token = getFreshToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json'
  };
};

// Checkout Form Component
const CheckoutForm = ({ clientSecret, onSuccess, onCancel, planName, amount, pricing, planId, billingCycle }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/subscription/success`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message);
      setIsLoading(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Payment successful - now call backend to create subscription
      setSuccessMessage('Payment successful! Activating your subscription...');
      
      try {
        const token = localStorage.getItem('streamToken');
        
        // Call confirm-payment endpoint to create subscription, payment record & notification
        const confirmResponse = await axios.post(
          `${API_BASE}/stripe/confirm-payment`,
          {
            payment_intent_id: paymentIntent.id,
            plan_id: planId,
            billing_cycle: billingCycle
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        const subscriptionData = confirmResponse.data?.subscription || null;
        setSuccessMessage('✓ Subscription activated successfully!');
        setTimeout(() => {
          // Pass subscriptionData as 4th arg so Pricing.jsx can use it
          onSuccess(paymentIntent, subscriptionData);
        }, 1500);
      } catch (confirmError) {
        console.error('Error confirming payment with backend:', confirmError);
        // Still consider it success since Stripe payment went through
        setSuccessMessage('✓ Payment successful! Your subscription will be activated shortly.');
        setTimeout(() => {
          onSuccess(paymentIntent, null);
        }, 2000);
      }
    } else {
      // Handle other statuses like requires_action, processing, etc.
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-checkout-form">
      {errorMessage && (
        <Alert variant="danger" className="mb-3">
          {errorMessage}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" className="mb-3 d-flex align-items-center">
          <CheckCircle size={20} className="me-2" />
          {successMessage}
        </Alert>
      )}
      
      <div className="payment-details mb-4">
        <div className="p-3 bg-light rounded">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-1">{planName}</h6>
              <small className="text-muted">Subscription</small>
            </div>
            <div className="text-end">
              <h5 className="mb-0">${((pricing ? pricing.subtotal : amount) / 100).toFixed(2)}</h5>
              <small className="text-muted">per period</small>
            </div>
          </div>
          {pricing && pricing.tax_lines && pricing.tax_lines.length > 0 && (
            <div className="mt-2 pt-2 border-top small">
              {pricing.tax_lines.map((t, i) => (
                <div key={i} className="d-flex justify-content-between text-muted">
                  <span>{t.name}{t.type === 'percentage' ? ` (${t.rate}%)` : ''}</span>
                  <span>${(t.amount / 100).toFixed(2)}</span>
                </div>
              ))}
              <div className="d-flex justify-content-between fw-bold mt-1">
                <span>Total</span>
                <span>${(pricing.total / 100).toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <PaymentElement 
        options={{
          layout: {
            type: 'tabs',
            defaultCollapsed: false,
          },
        }}
      />

      <div className="d-flex gap-2 mt-4">
        <Button
          variant="primary"
          type="submit"
          disabled={!stripe || isLoading}
          className="flex-fill"
        >
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Processing...
            </>
          ) : (
            `Pay $${(amount / 100).toFixed(2)}`
          )}
        </Button>
        <Button variant="outline-secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};


// Main Modal Component
const EmbeddedCheckoutModal = ({ show, onHide, plan, billingCycle, onSuccess }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use ref to track if we've already triggered payment intent creation
  const hasTriggeredRef = useRef(false);

  const createPaymentIntent = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setClientSecret(null);

    // Get FRESH token each time - this is critical!
    const currentToken = getFreshToken();
    console.log('[Checkout] Token check:', currentToken ? 'Token found' : 'NO TOKEN');

    if (!currentToken) {
      setError('Authentication required. Please log in and try again.');
      setIsLoading(false);
      return;
    }

    try {
      // Debug: Log what's being sent
      console.log('[Checkout] Creating payment intent with:', {
        plan: plan,
        planId: plan?.id,
        billingCycle: billingCycle,
        apiBase: API_BASE
      });

      // Get publishable key from Stripe public settings endpoint
      console.log('[Checkout] Fetching Stripe publishable key...');
      const settingsResponse = await axios.get(`${API_BASE}/settings/stripe-public`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[Checkout] Settings response:', settingsResponse.data);
      
      const stripeKey = settingsResponse.data?.stripe_publishable_key;
      
      if (!stripeKey) {
        throw new Error('Stripe not configured. Please contact administrator to set up Stripe keys.');
      }
      setPublishableKey(stripeKey);
      console.log('[Checkout] Got Stripe publishable key');

      // Create payment intent - send plan_id and billing_cycle
      const requestData = {
        plan_id: plan.id,
        billing_cycle: billingCycle
      };
      
      console.log('[Checkout] Sending payment intent request:', requestData);
      
      const response = await axios.post(
        `${API_BASE}/stripe/create-payment-intent`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      console.log('[Checkout] Payment intent response:', response.data);

      if (response.data.success) {
        setClientSecret(response.data.client_secret);
        setPricing(response.data.pricing || null);
        console.log('[Checkout] Got client_secret successfully');
      } else {
        throw new Error(response.data.error || 'Failed to initialize payment');
      }
    } catch (err) {
      console.error('[Checkout] Error creating payment intent:', err);
      console.error('[Checkout] Error details:', {
        response: err.response?.data,
        status: err.response?.status,
        request: err.request,
        message: err.message
      });
      
      // Provide more helpful error messages
      let errorMessage = err.response?.data?.error || err.message || 'Failed to initialize payment. Please try again.';
      
      if (err.response?.status === 401) {
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to make payments.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Payment service not found. Please contact administrator.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [plan, billingCycle]);

  useEffect(() => {
    console.log('[Checkout] useEffect fired:', { show, planId: plan?.id, hasTriggered: hasTriggeredRef.current });
    
    // Reset state when modal closes
    if (!show) {
      hasTriggeredRef.current = false;
      setClientSecret(null);
      setPublishableKey(null);
      setError(null);
      setIsLoading(false);
      console.log('[Checkout] Modal closed, reset state');
      return;
    }

    // Guard: must have a selected plan before initializing
    if (!plan?.id) {
      console.log('[Checkout] No plan yet, waiting...');
      setError('No plan selected. Please select a plan first.');
      setIsLoading(false);
      return;
    }

    // Initialize once per modal open
    if (!hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      console.log('[Checkout] Triggering createPaymentIntent');
      createPaymentIntent();
    }
  }, [show, plan, createPaymentIntent]);

  const handleSuccess = (paymentIntent, subscriptionData) => {
    if (onSuccess) {
      // Pass paymentIntent, plan, billingCycle, and subscriptionData to parent
      onSuccess(paymentIntent, plan, billingCycle, subscriptionData);
    }
    onHide();
  };

  const stripeOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#0570de',
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#df1b41',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '4px',
      },
    },
  };

  const amount = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      size="md"
      backdrop="static"
    >
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="w-100 text-center">
          Complete Your Subscription
        </Modal.Title>
        <Button 
          variant="link" 
          className="position-absolute end-0 top-0 mt-2 me-2 p-2"
          onClick={onHide}
        >
          <X size={20} />
        </Button>
      </Modal.Header>
      
      <Modal.Body className="pt-3">
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Initializing secure payment...</p>
            <small className="text-muted d-block mt-2">
              Please wait while we prepare your checkout
            </small>
          </div>
        ) : error ? (
          <div className="text-center py-4">
            <Alert variant="danger" className="d-flex align-items-start">
              <AlertTriangle size={20} className="me-2 mt-1" />
              <div>
                <h6 className="alert-heading">Payment Error</h6>
                <p className="mb-2">{error}</p>
                <small className="text-muted">
                  Please try again or contact support if the problem persists.
                </small>
              </div>
            </Alert>
            <div className="mt-3">
              <Button 
                variant="primary" 
                onClick={() => {
                  console.log('[Checkout] Retrying payment intent creation');
                  hasTriggeredRef.current = false;
                  createPaymentIntent();
                }}
                className="me-2"
              >
                Try Again
              </Button>
              <Button variant="outline-secondary" onClick={onHide}>
                Close
              </Button>
            </div>
          </div>
        ) : clientSecret && publishableKey ? (
          <Elements stripe={getStripe(publishableKey)} options={stripeOptions}>
            <CheckoutForm
              clientSecret={clientSecret}
              onSuccess={handleSuccess}
              onCancel={onHide}
              planName={plan.name}
              amount={pricing ? pricing.total : amount}
              pricing={pricing}
              planId={plan.id}
              billingCycle={billingCycle}
            />
          </Elements>
        ) : (
          <Alert variant="warning">
            Unable to load payment form. Please try again later.
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer className="border-0 pt-0 justify-content-center">
        <small className="text-muted text-center">
          <i className="fas fa-lock me-1"></i>
          Secure payment powered by Stripe. Your card details are encrypted.
        </small>
      </Modal.Footer>
    </Modal>
  );
};

export default EmbeddedCheckoutModal;
